import {
  DEFAULT_BRUSH_SIZE,
  STATE_KEY,
  type BackgroundRemoverItem,
  type BackgroundRemoverState,
  type UploadedMedia,
} from './types';
import { t } from './i18n';
import type { EngineSettings } from './engine/imglyConfig';

type ImageToolContext = {
  item: BackgroundRemoverItem;
  update: (patch: Record<string, unknown>) => void;
};

type VeyopFacade = {
  hooks?: {
    on: (event: string, handler: (payload: unknown) => void) => void;
    emit?: (event: string, payload?: unknown) => void;
  };
  tools?: {
    registerImageTool: (tool: {
      id: string;
      label: string;
      priority?: number;
      render: (ctx: ImageToolContext) => HTMLElement;
    }) => () => void;
  };
  itemRenderers?: {
    register: (renderer: {
      id: string;
      priority?: number;
      matches: (item: BackgroundRemoverItem) => boolean;
      render: (item: BackgroundRemoverItem, container: HTMLElement) => () => void;
    }) => () => void;
  };
  designer?: {
    beginHistoryBatch?: () => void;
    endHistoryBatch?: (reason?: string) => void;
    updateItem?: (id: string, patch: Record<string, unknown>) => void;
  };
};

let registered = false;
let runtime: VeyopFacade | null = null;
const touchedItems = new Set<BackgroundRemoverItem>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isImageItem(item: unknown): item is BackgroundRemoverItem {
  return isRecord(item) && item.type === 'image' && typeof item.src === 'string' && item.src.length > 0;
}

function stateOf(item: BackgroundRemoverItem): BackgroundRemoverState {
  return isRecord(item[STATE_KEY]) ? { ...(item[STATE_KEY] as BackgroundRemoverState) } : {};
}

function patchItem(item: BackgroundRemoverItem, patch: Record<string, unknown>): void {
  Object.assign(item, patch);
  touchedItems.add(item);
  runtime?.hooks?.emit?.('update.design', { property: 'item', value: item });
}

function commitItem(ctx: ImageToolContext, patch: Record<string, unknown>, reason = 'bg-remover'): void {
  touchedItems.add(ctx.item);
  const host = runtime?.designer ?? (window as unknown as { Veyop?: VeyopFacade }).Veyop?.designer;
  if (host?.updateItem && ctx.item.id) {
    host.beginHistoryBatch?.();
    host.updateItem(ctx.item.id, patch);
    host.endHistoryBatch?.(reason);
    return;
  }
  ctx.update(patch);
}

function setState(ctx: ImageToolContext, next: BackgroundRemoverState): void {
  ctx.update({ [STATE_KEY]: { ...next } });
  touchedItems.add(ctx.item);
}

function readDesignerConfig(): Record<string, unknown> {
  const el = document.getElementById('designer-config');
  if (!el) return {};
  try {
    const parsed = JSON.parse(el.textContent || '{}');
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function pluginBaseUrl(): string {
  const config = readDesignerConfig();
  const configured = String(config.pluginBaseUrl ?? config.veyopUrl ?? '').trim();
  if (configured) return configured.replace(/\/$/, '');

  const script = Array.from(document.scripts).find((node) => node.src.includes('/plugins/background-remover/'));
  if (script?.src) {
    const marker = '/plugins/background-remover/';
    return script.src.slice(0, script.src.indexOf(marker)).replace(/\/$/, '');
  }

  return window.location.origin;
}

function apiBaseUrl(): string {
  const config = readDesignerConfig();
  const configured = String(config.apiBaseUrl ?? '').trim();
  return (configured || pluginBaseUrl()).replace(/\/$/, '');
}

function uploadBaseUrl(): string {
  const config = readDesignerConfig();
  const configured = String(config.uploadBaseUrl ?? '').trim();
  if (configured) return configured.replace(/\/$/, '');
  return `${pluginBaseUrl()}/uploads`.replace(/\/$/, '');
}

function csrfToken(): string {
  const config = readDesignerConfig();
  return String(config.apiNonce ?? config.csrfToken ?? '').trim();
}

function backgroundRemovalPublicPath(): string {
  const config = readDesignerConfig();
  const configured = String(config.backgroundRemoverDataUrl ?? '').trim();
  const base = configured || 'https://staticimgly.com/@imgly/background-removal-data/1.7.0/dist';
  return `${base.replace(/\/$/, '')}/`;
}

type RuntimeUploadedImage = { url: string; large: string; thumb: string; file: string; name: string };

function runtimeImageUploader(): ((file: File) => Promise<RuntimeUploadedImage>) | null {
  const uploads = (window as Window & { Veyop?: { uploads?: { uploadImage?: (file: File) => Promise<RuntimeUploadedImage> } } }).Veyop?.uploads;
  return typeof uploads?.uploadImage === 'function' ? uploads.uploadImage.bind(uploads) : null;
}

async function uploadProcessedImage(blob: Blob, baseName = 'background-removed.png'): Promise<UploadedMedia> {
  const file = new File([blob], baseName.endsWith('.png') ? baseName : `${baseName}.png`, { type: 'image/png' });
  // Shopper upload: the same endpoint the designer uses, open to guests on every host.
  // The admin media API below rejects storefront shoppers, so it is only a fallback for
  // older cores that do not expose `Veyop.uploads.uploadImage`.
  const uploadImage = runtimeImageUploader();
  if (uploadImage) {
    const stored = await uploadImage(file);
    return { name: stored.name, url: stored.url, path: stored.file || stored.url, large: stored.large, thumb: stored.thumb };
  }
  const form = new FormData();
  form.append('file', file);
  const token = csrfToken();
  if (token) form.append('_token', token);

  const options: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { 'X-CSRF-Token': token } : undefined,
    body: form,
  };
  const portable = (window as Window & { Veyop?: { http?: { request<R>(path: string, options?: RequestInit): Promise<R> } } }).Veyop?.http;
  if (portable) return portable.request<UploadedMedia>('/api/v1/media/upload', options);
  const response = await fetch(`${apiBaseUrl()}/api/v1/media/upload`, options);
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.error) {
    throw new Error(String(json.msg || json.error || t('Upload failed')));
  }
  return json.data as UploadedMedia;
}

/** Layer fields for a stored transparent PNG; `large` drives print output, so it must follow `src`. */
function processedSourceFields(uploaded: UploadedMedia): Record<string, string> {
  return {
    src: uploaded.url,
    thumb: uploaded.thumb || uploaded.url,
    large: uploaded.large || uploaded.url,
    file: uploaded.path,
    file_name: uploaded.name,
  };
}

function originalSourceFields(state: BackgroundRemoverState): Record<string, string> {
  const src = state.originalSrc || '';
  return { src, thumb: src, large: state.originalLarge || src };
}

/**
 * The IMG.LY runtime bundled with the addon (dist/runtime/imgly-runtime.js), next to this
 * bundle. Built from a variable so Vite does not inline the 1.8 MB file into this one.
 */
const RUNTIME_FILE = 'runtime/imgly-runtime.js';
function bundledRuntimeUrl(): string {
  return new URL(RUNTIME_FILE, import.meta.url).href;
}
const MODELS = ['isnet', 'isnet_fp16', 'isnet_quint8'] as const;

function httpsUrl(value: unknown): string {
  return typeof value === 'string' && /^https:\/\//i.test(value) ? value : '';
}

function engineSettings(): EngineSettings {
  const config = readDesignerConfig();
  const hostConfig = (window as Window & { VeyopHostConfig?: { backgroundRemoverModuleUrl?: string } }).VeyopHostConfig;
  const model = String(config.backgroundRemoverModel ?? 'auto');
  return {
    moduleUrl: httpsUrl(config.backgroundRemoverModuleUrl) || httpsUrl(hostConfig?.backgroundRemoverModuleUrl) || bundledRuntimeUrl(),
    // The worker resolves this against its own URL, so it must be absolute.
    publicPath: new URL(backgroundRemovalPublicPath(), window.location.href).href,
    model: (MODELS as readonly string[]).includes(model) ? (model as EngineSettings['model']) : 'auto',
  };
}

function backgroundRemovalModelVersion(): string {
  const config = readDesignerConfig();
  return String(config.backgroundRemoverModelVersion ?? 'imgly-v1').trim() || 'imgly-v1';
}

/** Inference runs in a module worker unless a store turns it off with `backgroundRemoverWorker: false`. */
function backgroundRemoverUseWorker(): boolean {
  const raw = readDesignerConfig().backgroundRemoverWorker;
  return !(raw === false || raw === 0 || raw === '0' || raw === 'off');
}

async function loadSourceBlob(src: string): Promise<Blob> {
  const response = await fetch(src, { credentials: 'same-origin' }).catch(() => null);
  if (!response?.ok) throw new Error(t('Could not load image.'));
  return response.blob();
}

/** Warm the runtime and model once the shopper shows intent (opening the menu). */
function preloadBackgroundRemoval(): void {
  void import('./engine')
    .then((engine) => engine.preloadEngine(engineSettings(), backgroundRemoverUseWorker()))
    .catch(() => {
      // A failed preload is retried by the real run, which reports the error.
    });
}

async function removeBackgroundBlob(src: string): Promise<Blob> {
  const [engine, source] = await Promise.all([import('./engine'), loadSourceBlob(src)]);
  return engine.removeBackgroundWithEngine(source, {
    settings: engineSettings(),
    modelVersion: backgroundRemovalModelVersion(),
    useWorker: backgroundRemoverUseWorker(),
  });
}

/** Status line for an engine progress event, or '' when it does not change the message. */
function progressStatus(key: string, current: number, total: number): string {
  if (key.startsWith('fetch:/models/') && total > 0) {
    return `${t('Downloading AI model…')} ${Math.min(100, Math.round((current / total) * 100))}%`;
  }
  if (key.startsWith('compute:') || key.startsWith('compose:')) return t('Removing background...');
  return '';
}

// ── HD removal through AI Image Tools (remove.bg / PhotoRoom / custom service) ─

type CloudConfig = { removeBg?: boolean; maxInputPixels?: number };

/** AI Image Tools publishes this when a background-removal provider is set up. */
function cloudConfig(): CloudConfig | null {
  const items = readDesignerConfig().items;
  const flags = isRecord(items) ? items.aiImageTools : null;
  return isRecord(flags) && flags.removeBg === true ? (flags as CloudConfig) : null;
}

/** Scale down to the server's input limit (it rejects larger images to bound provider cost). */
async function fitToPixels(blob: Blob, maxPixels: number): Promise<Blob> {
  if (maxPixels <= 0) return blob;
  const bitmap = await createImageBitmap(blob);
  try {
    const pixels = bitmap.width * bitmap.height;
    if (pixels <= maxPixels) return blob;
    const scale = Math.sqrt(maxPixels / pixels) * 0.999;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(bitmap.width * scale));
    canvas.height = Math.max(1, Math.floor(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve) => canvas.toBlob((out) => resolve(out ?? blob), 'image/png'));
  } finally {
    bitmap.close();
  }
}

/**
 * The designer's CSRF pair: WordPress checks the `veyop_api_nonce` header, OpenCart and
 * PrestaShop the session token posted as `_token`.
 */
function designerCsrf(): { header: Record<string, string>; token: string } {
  const config = readDesignerConfig();
  const nonce = String(config.apiNonce ?? '').trim();
  return { header: nonce ? { 'X-CSRF-Token': nonce } : {}, token: String(config.csrfToken ?? '').trim() };
}

/** The server stores the result (cached by content hash), so there is nothing to upload after. */
async function cloudRemove(src: string): Promise<UploadedMedia> {
  const http = (window as Window & { Veyop?: { http?: { request<R>(path: string, options?: RequestInit): Promise<R> } } }).Veyop?.http;
  if (!http) throw new Error(t('HD background removal is unavailable.'));
  const source = await fitToPixels(await loadSourceBlob(src), Number(cloudConfig()?.maxInputPixels) || 0);
  const form = new FormData();
  form.append('file', new File([source], 'image', { type: source.type || 'image/png' }));
  const csrf = designerCsrf();
  if (csrf.token) form.append('_token', csrf.token);
  const result = await http.request<{ name: string; url: string; path: string }>('/ai-image-tools/remove-bg', {
    method: 'POST',
    body: form,
    headers: csrf.header,
  });
  return { name: result.name, url: result.url, path: result.path, large: result.url, thumb: result.url };
}

// Shared toolbar button styling — identical to the built-in image actions
// (Adjust / Effects / Crop / Replace) so Remove Background reads as a peer
// control rather than a bespoke floating chip.
const TOOLBAR_BTN_CLASS =
  'vptw:px-2! vptw:py-1! vptw:text-xs! vptw:rounded-xl! vptw:hover:bg-(--vs-surface-2)! vptw:transition! vptw:cursor-pointer! vptw:text-(--vs-ink-2)! vptw:flex! vptw:items-center! vptw:gap-1! vptw:shrink-0! vptw:p-0! vptw:border-0!';

const REMOVE_BG_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h4M4 4v4M20 4h-4M20 4v4M4 20h4M4 20v-4M20 20h-4M20 20v-4"/><circle cx="12" cy="12" r="3.2"/></svg>';

interface ToolRefs {
  trigger: HTMLButtonElement;
  remove: HTMLButtonElement;
  hd: HTMLButtonElement | null;
  erase: HTMLButtonElement;
  restore: HTMLButtonElement;
  menu: HTMLElement;
}

const toolRefs = new WeakMap<HTMLElement, ToolRefs>();
let activeMenuCleanup: (() => void) | null = null;

function closeActiveMenu(): void {
  if (activeMenuCleanup) {
    const fn = activeMenuCleanup;
    activeMenuCleanup = null;
    fn();
  }
}

function makeMenuItem(label: string, title: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.title = title;
  button.className =
    'vptw:flex! vptw:w-full! vptw:items-center! vptw:gap-2! vptw:px-3! vptw:py-2! vptw:text-xs! vptw:font-medium! vptw:text-left! vptw:text-(--vs-ink-2)! vptw:bg-transparent! vptw:border-0! vptw:rounded-lg! vptw:cursor-pointer! vptw:transition! vptw:hover:bg-(--vs-surface-2)! disabled:vptw:opacity-50! disabled:vptw:cursor-not-allowed!';
  return button;
}

function makeAgplNote(): HTMLElement {
  const note = document.createElement('div');
  note.className =
    'vptw:mt-1! vptw:border-t! vptw:border-(--vs-line)! vptw:px-3! vptw:pt-2! vptw:pb-1! vptw:text-[10px]! vptw:leading-snug! vptw:text-(--vs-ink-3)!';
  note.innerHTML =
    `${t('Uses')} <a href="https://www.gnu.org/licenses/agpl-3.0.html" target="_blank" rel="noopener" class="vptw:text-sky-500! vptw:underline!">AGPL</a> ${t('on-device background removal.')}`;
  return note;
}

function refreshTool(container: HTMLElement, ctx: ImageToolContext): void {
  const refs = toolRefs.get(container);
  if (!refs) return;
  const state = stateOf(ctx.item);

  refs.trigger.disabled = Boolean(state.processing);
  const triggerLabel = state.processing ? t('Removing…') : t('Remove BG');
  refs.trigger.innerHTML = `<span>${triggerLabel}</span>`;
  refs.trigger.style.color = state.processing || state.processedSrc ? 'var(--vs-accent, #007aff)' : '';

  refs.remove.disabled = Boolean(state.processing);
  refs.remove.textContent = state.processing ? t('Processing…') : t('Remove background');

  if (refs.hd) refs.hd.disabled = Boolean(state.processing);
  refs.erase.disabled = Boolean(state.processing);

  refs.restore.disabled = Boolean(state.processing);
  refs.restore.style.display = state.originalSrc ? 'flex' : 'none';
}

/** `cloud` sends the image to AI Image Tools' provider; `browser` runs the on-device model. */
async function processItem(ctx: ImageToolContext, toolEl: HTMLElement, mode: 'browser' | 'cloud' = 'browser'): Promise<void> {
  if (!isImageItem(ctx.item)) return;
  const before = stateOf(ctx.item);
  if (before.processing) return;

  const originalSrc = before.originalSrc || String(ctx.item.src || '');
  const originalLarge = before.originalLarge || String(ctx.item.large || '');
  const busyState: BackgroundRemoverState = {
    ...before,
    originalSrc,
    originalLarge,
    previewSrc: originalSrc,
    processing: true,
    manualActive: false,
    statusText: mode === 'cloud' ? t('Sending to the HD service…') : t('Preparing AI model...'),
    error: '',
  };
  setState(ctx, busyState);
  refreshTool(toolEl, ctx);

  let lastStatus = busyState.statusText;
  let stopProgress = () => {};
  if (mode === 'browser') {
    const { onEngineProgress } = await import('./engine');
    stopProgress = onEngineProgress((key, current, total) => {
      const statusText = progressStatus(key, current, total);
      if (!statusText || statusText === lastStatus) return;
      lastStatus = statusText;
      setState(ctx, { ...busyState, statusText });
    });
  }

  try {
    // `large` is the full-size original; `src` is the medium copy shown on canvas. Cutting
    // the medium copy would leave print output (`large`) at screen resolution.
    const source = String(ctx.item.large || ctx.item.src || originalSrc);
    let uploaded: UploadedMedia;
    if (mode === 'cloud') {
      uploaded = await cloudRemove(source);
    } else {
      const transparent = await removeBackgroundBlob(source);
      stopProgress();
      setState(ctx, { ...busyState, statusText: t('Saving transparent image...') });
      uploaded = await uploadProcessedImage(transparent, `background-removed-${Date.now()}.png`);
    }
    const nextState: BackgroundRemoverState = {
      ...busyState,
      processing: false,
      manualActive: false,
      processedSrc: uploaded.url,
      processedPath: uploaded.path,
      previewSrc: uploaded.url,
      statusText: t('Background removed'),
      error: '',
    };
    ctx.update({
      ...processedSourceFields(uploaded),
      [STATE_KEY]: {
        ...nextState,
        previewSrc: '',
        error: '',
      },
    });
  } catch (error) {
    stopProgress();
    const message = error instanceof Error ? t(error.message) : t('Background removal failed');
    setState(ctx, {
      ...busyState,
      processing: false,
      manualActive: true,
      error: `${message.replace(/[.\s]+$/, '')}. ${t('Use manual erase to finish this image.')}`,
      statusText: '',
      brushSize: before.brushSize || DEFAULT_BRUSH_SIZE,
    });
  } finally {
    refreshTool(toolEl, ctx);
  }
}

function restoreOriginal(ctx: ImageToolContext, toolEl: HTMLElement): void {
  const state = stateOf(ctx.item);
  if (!state.originalSrc) return;
  const nextState: BackgroundRemoverState = {
    ...state,
    processing: false,
    manualActive: false,
    processedSrc: '',
    processedPath: '',
    previewSrc: state.originalSrc,
    statusText: '',
    error: '',
  };
  ctx.update({ ...originalSourceFields(state), [STATE_KEY]: nextState });
  refreshTool(toolEl, ctx);
}

function enterManualErase(ctx: ImageToolContext, toolEl: HTMLElement): void {
  if (!isImageItem(ctx.item)) return;
  const state = stateOf(ctx.item);
  eraseSessions.delete(ctx.item);
  setState(ctx, {
    ...state,
    originalSrc: state.originalSrc || String(ctx.item.src || ''),
    originalLarge: state.originalLarge || String(ctx.item.large || ''),
    previewSrc: String(ctx.item.src || state.originalSrc || ''),
    processing: false,
    manualActive: true,
    statusText: '',
    brushSize: state.brushSize || DEFAULT_BRUSH_SIZE,
  });
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
    runtime?.hooks?.emit?.('veyop:image-editor:open', { itemId: ctx.item.id, mode: 'erase' });
  }
  refreshTool(toolEl, ctx);
}

function positionMenu(menu: HTMLElement, trigger: HTMLElement): void {
  const rect = trigger.getBoundingClientRect();
  const menuWidth = menu.offsetWidth || 208;
  let left = Math.round(rect.left);
  const maxLeft = window.innerWidth - menuWidth - 8;
  if (left > maxLeft) left = Math.max(8, maxLeft);
  menu.style.top = `${Math.round(rect.bottom + 6)}px`;
  menu.style.left = `${left}px`;
}

function toggleMenu(wrap: HTMLElement, trigger: HTMLButtonElement, ctx: ImageToolContext): void {
  const refs = toolRefs.get(wrap);
  if (!refs) return;
  const isOpen = refs.menu.dataset.open === '1';
  closeActiveMenu();
  if (isOpen) return;

  refreshTool(wrap, ctx);
  preloadBackgroundRemoval();
  // Mount to <body> while open: the toolbar uses backdrop-filter, which would
  // otherwise become the containing block for a position:fixed child and break
  // viewport-relative placement. Detaching on close keeps the DOM clean.
  document.body.appendChild(refs.menu);
  refs.menu.dataset.open = '1';
  positionMenu(refs.menu, trigger);

  const onDocPointer = (event: Event) => {
    const target = event.target as Node;
    if (!refs.menu.contains(target) && target !== trigger && !trigger.contains(target)) {
      closeActiveMenu();
    }
  };
  const onReposition = () => positionMenu(refs.menu, trigger);
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') closeActiveMenu();
  };
  setTimeout(() => document.addEventListener('pointerdown', onDocPointer, true), 0);
  window.addEventListener('resize', onReposition);
  window.addEventListener('scroll', onReposition, true);
  document.addEventListener('keydown', onKey);

  activeMenuCleanup = () => {
    document.removeEventListener('pointerdown', onDocPointer, true);
    window.removeEventListener('resize', onReposition);
    window.removeEventListener('scroll', onReposition, true);
    document.removeEventListener('keydown', onKey);
    refs.menu.dataset.open = '0';
    refs.menu.remove();
  };
}

function createRemoveBackgroundTool(ctx: ImageToolContext): HTMLElement {
  const wrap = document.createElement('span');
  wrap.style.display = isImageItem(ctx.item) ? 'inline-flex' : 'none';
  wrap.style.alignItems = 'center';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.title = t('Remove background');
  trigger.className = TOOLBAR_BTN_CLASS;
  trigger.style.height = '28px';
  trigger.dataset.bgTrigger = '1';
  trigger.setAttribute('aria-haspopup', 'menu');

  const menu = document.createElement('div');
  menu.dataset.bgMenu = '1';
  menu.dataset.open = '0';
  menu.style.position = 'fixed';
  menu.style.zIndex = '2147483600';
  menu.className =
    'vptw:min-w-[208px]! vptw:rounded-2xl! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-1.5! vptw:shadow-xl! vptw:ring-1! vptw:ring-(--vs-line)!';

  const remove = makeMenuItem(t('Remove background'), t('Remove background with AI'));
  remove.dataset.bgRemove = '1';
  remove.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeActiveMenu();
    void processItem(ctx, wrap);
  });

  // Only when AI Image Tools has a background-removal provider: each use is billed by it.
  const hd = cloudConfig() ? makeMenuItem(t('Remove background (HD)'), t('Sharper edges from an online service. The image is sent to that service.')) : null;
  if (hd) {
    hd.dataset.bgRemoveHd = '1';
    hd.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeActiveMenu();
      void processItem(ctx, wrap, 'cloud');
    });
  }

  const erase = makeMenuItem(t('Manual erase'), t('Erase areas by hand'));
  erase.dataset.bgErase = '1';
  erase.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeActiveMenu();
    enterManualErase(ctx, wrap);
  });

  const restore = makeMenuItem(t('Restore original'), t('Restore the original image'));
  restore.dataset.bgRestore = '1';
  restore.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeActiveMenu();
    restoreOriginal(ctx, wrap);
  });

  menu.append(...[remove, hd, erase, restore].filter((node): node is HTMLButtonElement => node !== null), makeAgplNote());

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleMenu(wrap, trigger, ctx);
  });

  wrap.append(trigger);
  toolRefs.set(wrap, { trigger, remove, hd, erase, restore, menu });
  refreshTool(wrap, ctx);
  return wrap;
}

function fitImageRect(canvasWidth: number, canvasHeight: number, imageWidth: number, imageHeight: number) {
  const scale = Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return {
    left: (canvasWidth - width) / 2,
    top: (canvasHeight - height) / 2,
    width,
    height,
  };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load image for manual erase'));
    image.src = src;
  });
}

// ── Manual erase / restore ───────────────────────────────────────────────────

type BrushMode = 'erase' | 'restore';

/** Points are fractions of the canvas, so a re-render at another size replays them in place. */
interface Stroke {
  mode: BrushMode;
  /** Brush diameter as a fraction of the canvas width. */
  size: number;
  points: Array<{ x: number; y: number }>;
}

interface EraseSession {
  strokes: Stroke[];
  redo: Stroke[];
  mode: BrushMode;
}

/** Strokes live with the item, not the DOM: the layer re-renders on every state patch. */
const eraseSessions = new WeakMap<BackgroundRemoverItem, EraseSession>();

function eraseSession(item: BackgroundRemoverItem): EraseSession {
  let session = eraseSessions.get(item);
  if (!session) {
    session = { strokes: [], redo: [], mode: 'erase' };
    eraseSessions.set(item, session);
  }
  return session;
}

/**
 * Paint `stroke` from point `from` on. Erase cuts alpha; restore paints the original photo
 * back through a pattern drawn with the same geometry as the working canvas.
 */
function paintStroke(ctx: CanvasRenderingContext2D, stroke: Stroke, original: CanvasImageSource | null, from = 0): void {
  const { width, height } = ctx.canvas;
  const size = Math.max(1, stroke.size * width);
  ctx.save();
  if (stroke.mode === 'restore') {
    const pattern = original ? ctx.createPattern(original, 'no-repeat') : null;
    if (!pattern) {
      ctx.restore();
      return;
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = pattern;
    ctx.fillStyle = pattern;
  } else {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = '#000';
    ctx.fillStyle = '#000';
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = size;
  const points = stroke.points;
  if (from === 0 && points.length) {
    ctx.beginPath();
    ctx.arc(points[0].x * width, points[0].y * height, size / 2, 0, Math.PI * 2);
    ctx.fill();
  }
  const start = Math.max(1, from);
  if (points.length > start) {
    ctx.beginPath();
    ctx.moveTo(points[start - 1].x * width, points[start - 1].y * height);
    for (let i = start; i < points.length; i++) ctx.lineTo(points[i].x * width, points[i].y * height);
    ctx.stroke();
  }
  ctx.restore();
}

interface EraseController {
  canUndo(): boolean;
  canRedo(): boolean;
  undo(): void;
  redo(): void;
  mode(): BrushMode;
  setMode(mode: BrushMode): void;
  /** The edited image at its own pixel size (the canvas may carry letterbox margins). */
  exportBlob(): Promise<Blob | null>;
}

function renderSpinner(parent: HTMLElement, message: string): void {
  const overlay = document.createElement('div');
  overlay.style.position = 'absolute';
  overlay.style.inset = '0';
  overlay.style.display = 'flex';
  overlay.style.flexDirection = 'column';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.gap = '8px';
  overlay.style.borderRadius = '10px';
  overlay.style.background = 'var(--vs-glass, rgba(255,255,255,0.74))';
  overlay.style.backdropFilter = 'blur(10px)';
  overlay.style.pointerEvents = 'none';
  overlay.style.boxShadow = 'inset 0 0 0 1px var(--vs-line, rgba(226,232,240,0.82))';

  const spinner = document.createElement('span');
  spinner.style.width = '22px';
  spinner.style.height = '22px';
  spinner.style.border = '2px solid color-mix(in srgb, var(--vs-accent, #007aff) 22%, transparent)';
  spinner.style.borderTopColor = 'var(--vs-accent, #007aff)';
  spinner.style.borderRadius = '999px';
  spinner.style.animation = 'veyopBgSpin 850ms linear infinite';

  const label = document.createElement('span');
  label.textContent = message;
  label.style.fontSize = '11px';
  label.style.fontWeight = '600';
  label.style.color = 'var(--vs-ink-2, #334155)';

  const hint = document.createElement('span');
  hint.textContent = t('Keep this page open');
  hint.style.fontSize = '10px';
  hint.style.fontWeight = '500';
  hint.style.color = 'var(--vs-ink-3, #64748b)';

  overlay.append(spinner, label, hint);
  parent.appendChild(overlay);
}

function ensureSpinStyle(): void {
  if (document.getElementById('veyop-bg-remover-style')) return;
  const style = document.createElement('style');
  style.id = 'veyop-bg-remover-style';
  style.textContent = '@keyframes veyopBgSpin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}

function makeOverlayButton(label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.style.border = '1px solid var(--vs-line, rgba(226,232,240,0.96))';
  button.style.borderRadius = '999px';
  button.style.background = 'var(--vs-surface, rgba(255,255,255,0.92))';
  button.style.color = 'var(--vs-ink-2, #334155)';
  button.style.fontSize = '11px';
  button.style.fontWeight = '700';
  button.style.padding = '6px 10px';
  button.style.boxShadow = '0 10px 30px rgba(15,23,42,0.14)';
  button.style.cursor = 'pointer';
  return button;
}

/**
 * Keep an overlay (control row, error pill) next to the manual-erase canvas without living
 * inside the layer: the print area clips its children, which hid Apply on small layers.
 * Mounted on the designer root so the `--vs-*` theme variables still apply; repositioned
 * every frame so zoom, pan and resize follow the layer. Returns the cleanup.
 */
function anchorToLayer(el: HTMLElement, canvas: HTMLCanvasElement, placement: 'above' | 'below', gap: number): () => void {
  const host = document.querySelector<HTMLElement>('[data-testid="designer-root"]') ?? document.body;
  el.style.position = 'fixed';
  el.style.left = '0';
  el.style.top = '0';
  el.style.zIndex = '60';
  host.appendChild(el);
  let frame = 0;
  const place = () => {
    const rect = canvas.getBoundingClientRect();
    if (!canvas.isConnected || rect.width < 1 || rect.height < 1) {
      el.style.visibility = 'hidden';
    } else {
      const margin = 8;
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, margin), window.innerWidth - width - margin);
      const below = rect.bottom + gap;
      const above = rect.top - gap - height;
      // The designer's floating image toolbar also hugs the layer (above, or below when there
      // is no room). Prefer the requested side, then the other, skipping any spot that leaves
      // the viewport or covers the toolbar.
      const toolbar = Array.from(document.querySelectorAll<HTMLElement>('[role="toolbar"].vs-tb'))
        .map((node) => node.getBoundingClientRect())
        .find((box) => box.width > 0 && box.height > 0);
      const hitsToolbar = (top: number) => Boolean(toolbar)
        && left < toolbar!.right && left + width > toolbar!.left
        && top < toolbar!.bottom && top + height > toolbar!.top;
      const fits = (top: number) => top >= margin && top + height <= window.innerHeight - margin;
      const candidates = placement === 'below' ? [below, above] : [above, below];
      let top = candidates.find((value) => fits(value) && !hitsToolbar(value))
        ?? candidates.find((value) => fits(value))
        ?? candidates[0];
      if (hitsToolbar(top) && toolbar) {
        // Both sides collide: step past the toolbar's far edge on the chosen side.
        top = top >= rect.top ? toolbar.bottom + margin : toolbar.top - margin - height;
      }
      el.style.left = `${Math.round(left)}px`;
      el.style.top = `${Math.round(top)}px`;
      el.style.visibility = 'visible';
    }
    frame = requestAnimationFrame(place);
  };
  place();
  return () => {
    cancelAnimationFrame(frame);
    el.remove();
  };
}

function renderManualControls(
  item: BackgroundRemoverItem,
  canvas: HTMLCanvasElement,
  controller: EraseController,
): { dispose: () => void; refresh: () => void } {
  const state = stateOf(item);
  const row = () => {
    const el = document.createElement('div');
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.gap = '6px';
    el.style.pointerEvents = 'auto';
    return el;
  };
  const tools = row();
  const actions = row();

  const eraseMode = makeOverlayButton(t('Erase'));
  const restoreMode = makeOverlayButton(t('Restore'));
  eraseMode.title = t('Erase areas by hand');
  restoreMode.title = t('Paint the original image back');
  const undo = makeOverlayButton(t('Undo'));
  const redo = makeOverlayButton(t('Redo'));
  const apply = makeOverlayButton(t('Apply'));
  const cancel = makeOverlayButton(t('Cancel'));
  const reset = makeOverlayButton(t('Reset'));
  reset.title = t('Restore the original image');
  const brush = document.createElement('input');
  brush.type = 'range';
  brush.min = '8';
  brush.max = '80';
  brush.step = '1';
  brush.value = String(state.brushSize || DEFAULT_BRUSH_SIZE);
  brush.title = t('Brush size');
  brush.style.width = '92px';
  brush.style.accentColor = 'var(--vs-accent, #007aff)';

  const refresh = () => {
    const mode = controller.mode();
    for (const [button, value] of [[eraseMode, 'erase'], [restoreMode, 'restore']] as const) {
      const active = mode === value;
      button.setAttribute('aria-pressed', String(active));
      button.style.background = active ? 'var(--vs-accent, #007aff)' : 'var(--vs-surface, rgba(255,255,255,0.92))';
      button.style.color = active ? '#fff' : 'var(--vs-ink-2, #334155)';
    }
    undo.disabled = !controller.canUndo();
    redo.disabled = !controller.canRedo();
    undo.style.opacity = undo.disabled ? '0.5' : '1';
    redo.style.opacity = redo.disabled ? '0.5' : '1';
  };

  const stopPress = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  eraseMode.addEventListener('click', () => controller.setMode('erase'));
  restoreMode.addEventListener('click', () => controller.setMode('restore'));
  undo.addEventListener('click', () => controller.undo());
  redo.addEventListener('click', () => controller.redo());
  brush.addEventListener('input', () => {
    patchItem(item, { [STATE_KEY]: { ...stateOf(item), brushSize: Number(brush.value) || DEFAULT_BRUSH_SIZE } });
  });
  cancel.addEventListener('click', () => {
    eraseSessions.delete(item);
    patchItem(item, { [STATE_KEY]: { ...stateOf(item), manualActive: false, error: '' } });
  });
  reset.addEventListener('click', () => {
    const latest = stateOf(item);
    if (!latest.originalSrc) return;
    eraseSessions.delete(item);
    patchItem(item, {
      ...originalSourceFields(latest),
      [STATE_KEY]: { ...latest, manualActive: false, processing: false, processedSrc: '', processedPath: '', error: '' },
    });
  });
  apply.addEventListener('click', () => {
    apply.disabled = true;
    apply.textContent = t('Saving...');
    void controller.exportBlob().then((blob) => {
      if (!blob) {
        patchItem(item, { [STATE_KEY]: { ...stateOf(item), error: t('Could not export erased image.') } });
        apply.disabled = false;
        apply.textContent = t('Apply');
        return;
      }
      void uploadProcessedImage(blob, `manual-erased-${Date.now()}.png`)
        .then((uploaded) => {
          const latest = stateOf(item);
          eraseSessions.delete(item);
          commitItem({ item, update: () => {} }, {
            ...processedSourceFields(uploaded),
            [STATE_KEY]: {
              ...latest,
              manualActive: false,
              processing: false,
              processedSrc: uploaded.url,
              processedPath: uploaded.path,
              previewSrc: '',
              error: '',
            },
          }, 'bg-erase-apply');
        })
        .catch((error) => {
          const message = error instanceof Error ? t(error.message) : t('Save failed');
          patchItem(item, { [STATE_KEY]: { ...stateOf(item), error: message } });
          apply.disabled = false;
          apply.textContent = t('Apply');
        });
    });
  });

  reset.style.display = state.originalSrc ? '' : 'none';
  tools.append(eraseMode, restoreMode, undo, redo, brush);
  actions.append(apply, cancel, reset);

  // One panel under the layer: the floating image toolbar sits above it, so a message
  // anchored there collided with the toolbar.
  const panel = document.createElement('div');
  panel.style.display = 'flex';
  panel.style.flexDirection = 'column';
  panel.style.alignItems = 'center';
  panel.style.gap = '6px';
  panel.style.pointerEvents = 'auto';
  panel.addEventListener('mousedown', stopPress);
  if (state.error) {
    const message = document.createElement('div');
    message.textContent = state.error;
    message.setAttribute('role', 'status');
    message.style.maxWidth = '300px';
    message.style.padding = '6px 12px';
    message.style.borderRadius = '14px';
    message.style.background = 'var(--vs-surface, rgba(255,255,255,0.94))';
    message.style.boxShadow = '0 10px 26px rgba(15,23,42,0.16)';
    message.style.color = 'var(--vs-ink-2, #475569)';
    message.style.fontSize = '11px';
    message.style.fontWeight = '600';
    message.style.lineHeight = '1.35';
    message.style.textAlign = 'center';
    panel.appendChild(message);
  }
  panel.append(tools, actions);
  refresh();
  return { dispose: anchorToLayer(panel, canvas, 'below', 12), refresh };
}

/** A ring the size of the brush that follows the pointer over the canvas. */
function brushCursor(canvas: HTMLCanvasElement, sizeCss: () => number): () => void {
  const host = document.querySelector<HTMLElement>('[data-testid="designer-root"]') ?? document.body;
  const ring = document.createElement('div');
  ring.style.position = 'fixed';
  ring.style.zIndex = '61';
  ring.style.pointerEvents = 'none';
  ring.style.borderRadius = '999px';
  ring.style.border = '1.5px solid #fff';
  ring.style.boxShadow = '0 0 0 1px rgba(15,23,42,0.55)';
  ring.style.display = 'none';
  host.appendChild(ring);
  const move = (event: PointerEvent) => {
    const size = sizeCss();
    ring.style.width = `${size}px`;
    ring.style.height = `${size}px`;
    ring.style.left = `${event.clientX - size / 2}px`;
    ring.style.top = `${event.clientY - size / 2}px`;
    ring.style.display = 'block';
  };
  const hide = () => {
    ring.style.display = 'none';
  };
  canvas.addEventListener('pointermove', move);
  canvas.addEventListener('pointerleave', hide);
  canvas.style.cursor = 'none';
  return () => {
    canvas.removeEventListener('pointermove', move);
    canvas.removeEventListener('pointerleave', hide);
    ring.remove();
  };
}

function renderBackgroundRemoverItem(item: BackgroundRemoverItem, container: HTMLElement): () => void {
  ensureSpinStyle();
  const state = stateOf(item);
  const cleanups: Array<() => void> = [];
  const root = document.createElement('div');
  root.style.position = 'relative';
  root.style.width = '100%';
  root.style.height = '100%';
  root.style.overflow = state.manualActive ? 'visible' : 'hidden';
  root.style.borderRadius = '4px';
  root.style.pointerEvents = state.manualActive ? 'auto' : 'none';

  container.style.width = '100%';
  container.style.height = '100%';
  container.style.position = 'relative';
  container.style.overflow = 'visible';
  container.style.pointerEvents = state.manualActive ? 'auto' : 'none';

  if (state.manualActive) {
    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.style.borderRadius = '4px';
    canvas.style.boxShadow = '0 0 0 2px var(--vs-accent, #007aff), 0 0 0 9999px rgba(15,23,42,0.08) inset';
    root.appendChild(canvas);

    const session = eraseSession(item);
    let current: Stroke | null = null;
    let captureTarget: HTMLCanvasElement | null = null;
    const ctx = canvas.getContext('2d');
    /** The image as it was when manual mode opened; undo replays strokes on top of it. */
    let base: HTMLCanvasElement | null = null;
    /** Source for the restore brush: the photo before any background removal. */
    let original: HTMLCanvasElement | null = null;
    /**
     * Where the image sits in the canvas, and its own size. The canvas follows the layer box,
     * whose aspect can differ slightly from the photo's; Apply crops back to the photo so the
     * saved file keeps its original pixel size instead of gaining transparent margins.
     */
    let imageRect: { left: number; top: number; width: number; height: number } | null = null;
    let imageSize: { width: number; height: number } | null = null;
    // The canvas is created before the layer is laid out. Measuring it then gives a 0×0 rect,
    // which used to shrink the canvas to 1×1 px and stretch one pixel of the photo over the
    // whole layer. Draw once it has a real size, at the photo's own resolution (capped), and let
    // CSS scale it: zooming the designer then stays sharp, and Apply saves a full-size image
    // instead of one as small as the layer is on screen.
    const MAX_CANVAS_SIDE = 4096;
    let drawn = false;
    let loading = false;
    let retryFrames = 0;
    const reportDrawError = (error: unknown) => {
      const message = error instanceof Error ? t(error.message) : t('Could not load image.');
      patchItem(item, { [STATE_KEY]: { ...stateOf(item), error: message } });
    };
    const layer = (image: HTMLImageElement) => {
      const out = document.createElement('canvas');
      out.width = canvas.width;
      out.height = canvas.height;
      const fit = fitImageRect(out.width, out.height, image.naturalWidth || image.width, image.naturalHeight || image.height);
      out.getContext('2d')?.drawImage(image, fit.left, fit.top, fit.width, fit.height);
      return out;
    };
    const redraw = () => {
      if (!ctx || !base) return;
      ctx.save();
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(base, 0, 0);
      ctx.restore();
      for (const stroke of session.strokes) paintStroke(ctx, stroke, original);
    };
    const resizeAndDraw = async () => {
      if (!ctx || drawn || loading) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) {
        // Not laid out yet. ResizeObserver will call again; without it, retry for ~1 s.
        if (typeof ResizeObserver === 'undefined' && retryFrames++ < 60) {
          requestAnimationFrame(() => void resizeAndDraw().catch(reportDrawError));
        }
        return;
      }
      loading = true;
      try {
        // `large` is the full-size original; `src` is the medium copy shown on canvas.
        const currentSrc = String(item.large || item.src || state.originalSrc || '');
        const image = await loadImage(currentSrc);
        const naturalWidth = image.naturalWidth || image.width;
        const naturalHeight = image.naturalHeight || image.height;
        const box = canvas.getBoundingClientRect();
        const shown = fitImageRect(box.width, box.height, naturalWidth, naturalHeight);
        const scale = Math.min(
          Math.max(naturalWidth / Math.max(1, shown.width), window.devicePixelRatio || 1),
          MAX_CANVAS_SIDE / Math.max(box.width, box.height),
        );
        canvas.width = Math.max(1, Math.round(box.width * scale));
        canvas.height = Math.max(1, Math.round(box.height * scale));
        base = layer(image);
        imageRect = fitImageRect(canvas.width, canvas.height, naturalWidth, naturalHeight);
        imageSize = { width: naturalWidth, height: naturalHeight };
        const originalSrc = String(state.originalLarge || state.originalSrc || '');
        original = base;
        if (originalSrc && originalSrc !== currentSrc) {
          // Without the original the restore brush paints the current image back, which still
          // undoes erase strokes; it just cannot bring back what the AI removed.
          original = await loadImage(originalSrc).then(layer, () => base);
        }
        drawn = true;
        redraw();
      } finally {
        loading = false;
      }
    };
    /** Brush size is set in screen pixels; convert it to canvas pixels at the current zoom. */
    const brushFraction = () => {
      const rect = canvas.getBoundingClientRect();
      const perCssPixel = rect.width > 0 ? canvas.width / rect.width : (window.devicePixelRatio || 1);
      return (Number(stateOf(item).brushSize || DEFAULT_BRUSH_SIZE) * perCssPixel) / Math.max(1, canvas.width);
    };

    let controls: { dispose: () => void; refresh: () => void } | null = null;
    const controller: EraseController = {
      canUndo: () => session.strokes.length > 0,
      canRedo: () => session.redo.length > 0,
      undo() {
        const stroke = session.strokes.pop();
        if (!stroke) return;
        session.redo.push(stroke);
        redraw();
        controls?.refresh();
      },
      redo() {
        const stroke = session.redo.pop();
        if (!stroke) return;
        session.strokes.push(stroke);
        redraw();
        controls?.refresh();
      },
      mode: () => session.mode,
      setMode(mode) {
        session.mode = mode;
        controls?.refresh();
      },
      exportBlob() {
        // Not drawn yet (layout pending or image still loading): never save a blank canvas.
        if (!drawn || !imageRect || !imageSize) return Promise.resolve(null);
        const out = document.createElement('canvas');
        out.width = imageSize.width;
        out.height = imageSize.height;
        const outCtx = out.getContext('2d');
        if (!outCtx) return Promise.resolve(null);
        outCtx.imageSmoothingQuality = 'high';
        const { left, top, width, height } = imageRect;
        outCtx.drawImage(canvas, left, top, width, height, 0, 0, out.width, out.height);
        return new Promise((resolve) => out.toBlob(resolve, 'image/png'));
      },
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => void resizeAndDraw().catch(reportDrawError))
      : null;
    resizeObserver?.observe(canvas);
    cleanups.push(() => resizeObserver?.disconnect());
    void resizeAndDraw().catch(reportDrawError);

    const pointFromEvent = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) / Math.max(1, rect.width),
        y: (event.clientY - rect.top) / Math.max(1, rect.height),
      };
    };

    const onDown = (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!ctx || !drawn) return;
      captureTarget = canvas;
      canvas.setPointerCapture(event.pointerId);
      current = { mode: session.mode, size: brushFraction(), points: [pointFromEvent(event)] };
      paintStroke(ctx, current, original);
    };
    const onMove = (event: PointerEvent) => {
      if (!current || !ctx) return;
      event.preventDefault();
      current.points.push(pointFromEvent(event));
      paintStroke(ctx, current, original, current.points.length - 1);
    };
    const onUp = (event: PointerEvent) => {
      if (!current) return;
      session.strokes.push(current);
      session.redo = [];
      current = null;
      if (captureTarget?.hasPointerCapture(event.pointerId)) {
        captureTarget.releasePointerCapture(event.pointerId);
      }
      captureTarget = null;
      controls?.refresh();
    };
    /** Undo/redo strokes while erasing; capture phase so the designer's own undo does not fire. */
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      event.preventDefault();
      event.stopPropagation();
      if (key === 'y' || event.shiftKey) controller.redo();
      else controller.undo();
    };

    canvas.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey, true);
    canvas.style.touchAction = 'none';
    cleanups.push(() => canvas.removeEventListener('pointerdown', onDown));
    cleanups.push(() => window.removeEventListener('pointermove', onMove));
    cleanups.push(() => window.removeEventListener('pointerup', onUp));
    cleanups.push(() => window.removeEventListener('pointercancel', onUp));
    cleanups.push(() => window.removeEventListener('keydown', onKey, true));
    cleanups.push(brushCursor(canvas, () => Number(stateOf(item).brushSize || DEFAULT_BRUSH_SIZE)));

    container.appendChild(root);
    controls = renderManualControls(item, canvas, controller);
    cleanups.push(() => controls?.dispose());
  } else {
    const image = document.createElement('img');
    image.src = String(state.previewSrc || state.originalSrc || item.src || '');
    image.alt = 'image';
    image.draggable = false;
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'contain';
    image.style.display = 'block';
    image.style.pointerEvents = 'none';
    image.style.opacity = state.processing ? '0.78' : '1';
    image.style.filter = state.processing ? 'saturate(0.92)' : 'none';
    root.appendChild(image);

    if (state.processing) {
      renderSpinner(root, state.statusText || 'Removing background...');
    }
    container.appendChild(root);
  }

  return () => {
    cleanups.forEach((cleanup) => cleanup());
    container.replaceChildren();
  };
}

function register(Veyop: VeyopFacade): void {
  if (registered) return;
  if (!Veyop.tools?.registerImageTool || !Veyop.itemRenderers?.register) return;
  registered = true;
  runtime = Veyop;

  Veyop.tools.registerImageTool({
    id: 'background-remover.remove-background',
    label: t('Remove Background'),
    priority: 10,
    render: createRemoveBackgroundTool,
  });

  Veyop.itemRenderers.register({
    id: 'background-remover.image-state',
    priority: 4,
    matches: (item) => {
      if (!isImageItem(item)) return false;
      const state = stateOf(item);
      return Boolean(state.processing || state.manualActive || (state.error && !state.processedSrc));
    },
    render: renderBackgroundRemoverItem,
  });

  Veyop.hooks?.on('before.save.design', () => {
    for (const item of touchedItems) {
      const state = stateOf(item);
      if (!state.processing && !state.manualActive) continue;
      item[STATE_KEY] = { ...state, processing: false, manualActive: false };
    }
  });
}

function boot(): void {
  const tryRegister = () => {
    const Veyop = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
    if (Veyop) register(Veyop);
  };

  tryRegister();

  const start = Date.now();
  const tick = () => {
    tryRegister();
    if (registered || Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
